import { IComponentOptions, IQService } from "angular";
import { StateService, TransitionService } from "@uirouter/angularjs";
import { debounceTime, distinctUntilChanged, take } from "rxjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { getToken } from "@gtmhub/auth";
import { GtmhubController } from "@gtmhub/core";
import { storage } from "@gtmhub/core/storage";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { ISubTitle } from "@gtmhub/shared/components/ui/top-navigation-bar/top-nav-bar.component";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { IEnrichedTeam, ITeam, ITeamsStoreState, TeamService, TeamsActions } from "@gtmhub/teams";
import { ITeamEventArgs, TeamEvents } from "@gtmhub/teams/events";
import { addAssigneeDataToTeams, addAssigneeDataToTeamsMap } from "@gtmhub/teams/redux/teams-actions";
import { CurrentUserService } from "@gtmhub/users";
import { IPermissionsStoreState, hasPermissions } from "@gtmhub/users/redux";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { ModuleFlag } from "@webapp/feature-toggles/models/feature-module.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { IsNavigationExpandedCache } from "@webapp/navigation/services/is-navigation-expanded.cache";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { CheckInSettings, CheckInTemplate } from "@webapp/reflections/models/checkins-models";
import { isCheckInsNewLabelVisible } from "@webapp/reflections/utils/is-check-ins-new-label-visible";
import { ResizeObservable } from "@webapp/shared/utils/utils";
import { StatusChangePayload } from "@webapp/teams/models/status-actions.models";
import { createBreadCrumb } from "./utils/utils";

const teamProfileActiveTab = ["details", "check-ins"] as const;
type TeamProfileActveTab = (typeof teamProfileActiveTab)[number];
const DEFAULT_ACTIVE_TAB = "details";

interface ITeamProfileStateParamsService {
  teamId: string;
  focusSelectManagerField?: boolean;
  activeTab: TeamProfileActveTab;
  cadenceStart: string;
  memberId: string;
}

const bindStateToCtrl = (state: ITeamsStoreState & IAssigneesStoreState) => {
  const { map: teams, isFetched: teamsFetched, error: teamsError } = state.teams;
  const { map: assignees, isFetched: assigneesFetched, error: assigneesError } = state.assignees;

  return {
    teams: addAssigneeDataToTeamsMap(teams, assignees),
    assignees,
    reduxItemsFetched: teamsFetched && assigneesFetched,
    reduxItemsError: teamsError || assigneesError,
  };
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface TeamProfileCtrl extends ReturnType<typeof bindStateToCtrl> {}
export class TeamProfileCtrl extends GtmhubController {
  private teamEvents: TeamEvents;
  team: IEnrichedTeam;
  token: string;
  indicators: {
    loading: boolean;
  };
  isReflectionsModuleFeatureEnabled: boolean;
  isCheckInUxImprovementsEnabled: boolean;
  isCheckInsFeatureAvailableForCurrentPlan: boolean;
  activeTabIndex: { [activeTab in TeamProfileActveTab]: number };
  public checkInSettings: CheckInSettings;
  public checkInTemplate: CheckInTemplate;
  public activeTab: TeamProfileActveTab;
  public focusSelectManagerField: boolean;
  public hasPermissionAccessPeople: boolean;
  public hasPermissionManagePeople: boolean;
  public hasPermissionManageReflections: boolean;
  public breadCrumb: ISubTitle;

  showNameEditor = false;

  public favoritesManager: FavoritesManager;
  public isViewOnlyRole: boolean;
  public shouldCheckInsNewLabelBeVisible: boolean;

  static $inject = [
    "$rootScope",
    "$scope",
    "$ngRedux",
    "TeamsActions",
    "AssigneeActions",
    "$stateParams",
    "TeamService",
    "UIErrorHandlingService",
    "$state",
    "FeatureTogglesFacade",
    "FavoritesManagerFactory",
    "AccountService",
    "$q",
    "CurrentUserService",
    "AnalyticsService",
    "IsNavigationExpandedCache",
    "$transitions",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $scope: IExtendedRootScopeService,
    $ngRedux: INgRedux,
    teamsActions: TeamsActions,
    assigneeActions: AssigneeActions,
    private $stateParams: ITeamProfileStateParamsService,
    private teamService: TeamService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $state: StateService,
    private featureToggleService: FeatureTogglesFacade,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private accountService: AccountService,
    private $q: IQService,
    private currentUserService: CurrentUserService,
    private analyticsService: AnalyticsService,
    private isNavigationExpandedCache: IsNavigationExpandedCache,
    private $transitions: TransitionService
  ) {
    super();
    this.focusSelectManagerField = !!this.$stateParams.focusSelectManagerField;
    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(teamsActions.fetchTeamsIfMissing());
    $ngRedux.dispatch(assigneeActions.getAssignees());

    this.teamEvents = new TeamEvents(this.$scope);
    this.favoritesManager = this.favoritesManagerFactory(["user", "team"]);
    this.isViewOnlyRole = this.$rootScope.hasRole("view-only");
    this.hasPermissionAccessPeople = hasPermissions($ngRedux.getState<IPermissionsStoreState>(), "AccessPeople");
    this.hasPermissionManagePeople = hasPermissions($ngRedux.getState<IPermissionsStoreState>(), "ManagePeople");
    this.hasPermissionManageReflections = hasPermissions($ngRedux.getState<IPermissionsStoreState>(), "ManageReflections");
  }

  $onInit(): void {
    this.indicators = {
      loading: true,
    };

    this.activeTabIndex = {
      details: 0,
      "check-ins": 1,
    };

    this.breadCrumb = createBreadCrumb(this.$state.current.name);

    this.teamEvents.onTeamUpdated((args) => this.onTeamUpdated(args));

    this.token = getToken();

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.reduxItemsFetched,
        () => this.reduxItemsError
      )
      .then(() => {
        this.$q
          .all({
            checkInUxImprovementsEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.CheckInsUxImprovements),
            reflectionsModuleEnabled: this.featureToggleService.isFeatureAvailable(ModuleFlag.Reflections),
            teamsCollection: this.teamService.getTeams({ filter: { _id: this.$stateParams.teamId }, fields: "id,badges,customFields,description" }),
          })
          .then(({ checkInUxImprovementsEnabled, reflectionsModuleEnabled, teamsCollection }) => {
            // there always be only one element in the items
            const team = teamsCollection.items[0];
            this.team = { ...this.teams[this.$stateParams.teamId], ...team, type: "team" };

            this.teamEvents.broadcastTeamVisited(this.team);
            this.isReflectionsModuleFeatureEnabled = reflectionsModuleEnabled;
            this.isCheckInUxImprovementsEnabled = checkInUxImprovementsEnabled;

            if (this.isReflectionsModuleFeatureEnabled) {
              this.toggleIsNavExpandedOnResize();
              this.watchForStateChanges();
            }

            this.indicators.loading = false;

            const isActiveTabValid = this.$stateParams.activeTab && teamProfileActiveTab.includes(this.$stateParams.activeTab);
            if (isActiveTabValid) {
              this.activeTab = this.$stateParams.activeTab;
              this.trackActiveTab(this.activeTab);
            } else {
              this.changeActiveTab(DEFAULT_ACTIVE_TAB, { replaceLocation: true });
            }

            this.favoritesManager.initFavorites();
          });
      })
      .catch((error) => {
        this.indicators.loading = false;
        this.uiErrorHandlingService.handleModal(error);
      });

    const areCheckInsEnabled = this.accountService.featureAvailable(FeatureFlag.FeatureReflections);
    this.isCheckInsFeatureAvailableForCurrentPlan = areCheckInsEnabled;
    this.shouldCheckInsNewLabelBeVisible = isCheckInsNewLabelVisible({ areCheckInsEnabled, currentUserService: this.currentUserService });
  }

  public updateField(field: keyof ITeam, value: string | string[]): void {
    const team: Partial<ITeam> = {
      id: this.team.id,
      [field]: value,
    };

    this.teamService.updateTeam(team).then(
      (updated) => {
        const enrichedUpdateTeam = addAssigneeDataToTeams([updated], this.assignees)[0];
        const team = Object.assign(this.team, enrichedUpdateTeam);
        this.team = structuredClone(team as IEnrichedTeam);
        this.teamEvents.broadcastTeamUpdated({ team: this.team });
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  }

  public openAvatarSelector = (): void => {
    this.$state.go("._form.form", { teamId: this.team.id, "avatar-selector-only": true });
  };

  public handleTeamStatusChange(payload: StatusChangePayload): void {
    this.broadcastTeamUpdated(payload);
    this.returnToState();
  }

  public handleTeamDeleted(): void {
    this.broadcastTeamDeleted();
    this.returnToState();
  }

  public broadcastTeamUpdated(payload: StatusChangePayload): void {
    if (!payload.hasFailures) {
      this.teamEvents.broadcastTeamUpdated({ team: { ...this.team, isActive: payload.action === "activate" } });
    }
  }

  public broadcastTeamDeleted(): void {
    this.teamEvents.broadcastTeamDeleted({ teamId: this.team.id, parentId: this.team.parentId });
  }

  public returnToState(): void {
    this.$state.go("^");
  }

  public addToFavorites(): void {
    this.favoritesManager.addToFavorites(this.team, "team", this.getNavItem());
  }

  public removeFromFavorites(): void {
    this.favoritesManager.removeFromFavorites(this.team, this.getNavItem());
  }

  private onTeamUpdated = (args: ITeamEventArgs): void => {
    this.team = { ...this.team, ...args.team, type: "team" };
  };

  public getNavItem = (): INavItem => {
    if (!this.team) return undefined;
    return this.favoritesManager.getFavoriteItem({ id: this.team.id }) || navItemsFactory.buildTeamNavItem(this.team);
  };

  public teamIsFavorite = (): boolean => {
    if (!this.team) return false;

    return this.favoritesManager.entityIsFavorite({ id: this.team.id });
  };

  public changeActiveTab = (activeTab: "details" | "check-ins", params: { replaceLocation: boolean } = { replaceLocation: true }): void => {
    if (activeTab === this.activeTab) return;

    this.trackActiveTab(activeTab);

    this.activeTab = activeTab;
    this.$state.go(
      ".",
      { ...this.$stateParams, activeTab, memberId: null, cadenceStart: null },
      { reload: false, notify: false, location: params.replaceLocation ? "replace" : true }
    );
  };

  private trackActiveTab(activeTab: TeamProfileActveTab): void {
    const eventName = activeTab === "check-ins" ? "Check-ins" : "Team Details";
    this.analyticsService.page(eventName, {
      controller: "TeamProfileCtrl",
    });
  }

  private toggleIsNavExpandedOnResize(): void {
    const resizeCallback = (res) => {
      this.isNavigationExpandedCache
        .get$()
        .pipe(take(1))
        .subscribe((isNavExpanded) => {
          if (res[0].contentRect.width <= 1280 && isNavExpanded) {
            this.isNavigationExpandedCache.set({ value: false, persistValue: false });
          } else if (storage.get("isNavExpanded") && !isNavExpanded) {
            this.isNavigationExpandedCache.set({ value: true, persistValue: false });
          }
        });
    };

    const resizeSubscription = new ResizeObservable(document.body).pipe(debounceTime(200), distinctUntilChanged()).subscribe(resizeCallback);
    this.$scope.$on("$destroy", () => {
      resizeSubscription.unsubscribe();
    });
  }

  private watchForStateChanges = () => {
    const unsubsrcribeStateChange = this.$transitions.onSuccess({}, () => {
      this.isNavigationExpandedCache
        .get$()
        .pipe(take(1))
        .subscribe((isNavExpanded) => {
          if (document.body.clientWidth <= 1280 && isNavExpanded) {
            this.isNavigationExpandedCache.set({ value: false, persistValue: false });
          }
        });
    });
    this.$scope.$on("$destroy", unsubsrcribeStateChange);
  };
}

export const TeamProfileComponent: IComponentOptions = {
  controller: TeamProfileCtrl,
  template: require("./team-profile.component.html"),
};
